import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { ADMIN_METHODOLOGY_COMPOSITION_UI_ENABLED } from "@/config/compositionUiFlags";
import OverviewTab from "@/components/admin/methodology/OverviewTab";
import TaxonomyTab from "@/components/admin/methodology/TaxonomyTab";
import ModulesTab from "@/components/admin/methodology/ModulesTab";
import QuestionsTab from "@/components/admin/methodology/QuestionsTab";
import CatalogTab from "@/components/admin/methodology/CatalogTab";
import PublicationReviewsTab from "@/components/admin/methodology/PublicationReviewsTab";
import BlueprintsTab from "@/components/admin/methodology/BlueprintsTab";
import RuntimeTab from "@/components/admin/methodology/RuntimeTab";
import ManifestsTab from "@/components/admin/methodology/ManifestsTab";
import AssessmentSafetyTab from "@/components/admin/methodology/AssessmentSafetyTab";

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "taxonomy", label: "Taxonomy" },
  { value: "modules", label: "Modules" },
  { value: "questions", label: "Questions" },
  { value: "mappings", label: "Control Mappings" },
  { value: "reviews", label: "Publication Reviews" },
  { value: "blueprints", label: "Blueprints" },
  { value: "runtime", label: "Runtime" },
  { value: "manifests", label: "Assessment Manifests" },
  { value: "report-safety", label: "Report Safety" },
  { value: "governance", label: "Governance & Eligibility" },
];

export default function MethodologyCompositionConsole() {
  const { loading, isAdmin } = useIsAdmin();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (TABS.some((t) => t.value === hash)) setTab(hash);
  }, []);

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Checking admin access…</div>;
  }

  if (!ADMIN_METHODOLOGY_COMPOSITION_UI_ENABLED) {
    return (
      <div className="p-6">
        <p className="text-destructive">
          The Methodology &amp; Composition console is not exposed in this build.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          This is a UI-visibility setting only
          (VITE_ADMIN_METHODOLOGY_COMPOSITION_UI). It is not an authorization
          decision and it does not change any operational flag.
        </p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-destructive">
          You do not have access to the Methodology &amp; Composition console.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          This console is restricted to platform administrators. Access is enforced by the
          server on every request.
        </p>
      </div>
    );
  }


  return (
    <AppLayout
      title="Methodology & Composition | CSV Pro Admin"
      description="Administrator console for inspecting methodology artifacts, publication readiness, blueprints, composition runtime state and report safety."
    >
      <Alert className="mb-6 border-destructive bg-destructive/10">
        <AlertDescription className="font-semibold text-destructive">
          DEV METHODOLOGY CONSOLE — NOT CLIENT-FACING — GLOBAL COMPOSITION REMAINS DISABLED
        </AlertDescription>
      </Alert>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link
            to="/admin"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            ← Back to Admin Console
          </Link>
          <h1 className="mt-2 text-3xl font-bold">Methodology &amp; Composition</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Read-first operator console. Every lifecycle status, readiness result and hash is
            computed by the server; nothing on this screen is authoritative.
          </p>
        </div>
        <Badge variant="secondary">Admin</Badge>
      </div>


      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v);
          window.history.replaceState(null, "", `#${v}`);
        }}
        className="space-y-4"
      >
        <TabsList className="flex h-auto flex-wrap justify-start">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab />
        </TabsContent>
        <TabsContent value="taxonomy">
          <TaxonomyTab />
        </TabsContent>
        <TabsContent value="modules">
          <ModulesTab />
        </TabsContent>
        <TabsContent value="questions">
          <QuestionsTab />
        </TabsContent>
        <TabsContent value="mappings">
          <CatalogTab />
        </TabsContent>
        <TabsContent value="reviews">
          <PublicationReviewsTab />
        </TabsContent>
        <TabsContent value="blueprints">
          <BlueprintsTab />
        </TabsContent>
        <TabsContent value="runtime">
          <RuntimeTab />
        </TabsContent>
        <TabsContent value="manifests">
          <ManifestsTab />
        </TabsContent>
        <TabsContent value="report-safety">
          <AssessmentSafetyTab mode="report" />
        </TabsContent>
        <TabsContent value="governance">
          <AssessmentSafetyTab mode="governance" />
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
